import React from 'react';
import { formatDistanceStrict } from 'date-fns';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss, Inline, Text } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import {
	PopupContainer,
	PopupTitle,
	PopupHeader,
	LinkContainer,
	PopupKeyline,
	PopupOverflowButton,
	PopupOverflowSection,
} from '@atlassian/jira-development-board-common/src/styled.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { deploymentTooltipContent_softwareReleasesLaunchCard_deployment$key } from '@atlassian/jira-relay/src/__generated__/deploymentTooltipContent_softwareReleasesLaunchCard_deployment.graphql';
import type { deploymentTooltipContent_softwareReleasesLaunchCard_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/deploymentTooltipContent_softwareReleasesLaunchCard_jiraVersion.graphql';
import { DeploymentIcons } from '@atlassian/jira-software-deployments-icons-common/src/ui/index.tsx';
import { Link } from '@atlassian/react-resource-router';
import { useDeploymentsRoute } from '../../../common/ui/deployments-page-link/index.tsx';
import messages from './messages.tsx';
import { mapDeploymentState, mapStateToLozengeProp, capitalizeOnlyFirstLetter } from './utils.tsx';

type Props = {
	deployment: deploymentTooltipContent_softwareReleasesLaunchCard_deployment$key;
	jiraVersion: deploymentTooltipContent_softwareReleasesLaunchCard_jiraVersion$key;
};

export const DeploymentTooltipContent = ({ deployment, jiraVersion }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment deploymentTooltipContent_softwareReleasesLaunchCard_jiraVersion on JiraVersion {
				name @required(action: THROW)
				project @required(action: THROW) {
					key @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const { lastUpdated, environment, state, issueIds } = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment deploymentTooltipContent_softwareReleasesLaunchCard_deployment on DeploymentSummary {
				state @required(action: THROW)
				environment @required(action: THROW) {
					displayName
					category @required(action: THROW)
				}
				lastUpdated @required(action: THROW)
				issueIds
			}
		`,
		deployment,
	);

	const deploymentsRoute = useDeploymentsRoute();

	return (
		<PopupContainer>
			<PopupHeader>
				<PopupTitle>{formatMessage(messages.deploymentPopupTitle)}</PopupTitle>
				<Inline xcss={headerStatusIconAndLozengeStyles}>
					<Inline xcss={headerStatusIconStyles}>
						<DeploymentIcons
							deploymentState={mapDeploymentState(state, environment.category)}
							size="medium"
						/>
					</Inline>
					<Lozenge appearance={mapStateToLozengeProp(state)}>{state}</Lozenge>
				</Inline>
			</PopupHeader>
			<LinkContainer>
				<Box xcss={environmentStyles}>
					<Text as="p" weight="bold">
						{capitalizeOnlyFirstLetter(environment.category)}
						{environment.displayName ? ` (${environment.displayName})` : ''}
					</Text>
				</Box>
			</LinkContainer>
			<Inline xcss={environmentDetailStyles}>
				<Text as="p" size="small">
					<span>
						{formatDistanceStrict(new Date(lastUpdated), new Date(), {
							addSuffix: true,
							roundingMethod: 'floor',
						})}
					</span>
					{issueIds == null ? null : (
						<>
							<Box as="span" xcss={environmentDetailBetweenMarginStyles}>
								•
							</Box>
							<Box as="span" xcss={environmentDetailBetweenMarginStyles}>
								<Text size="small">
									{formatMessage(
										fg('jira-issue-terminology-update-m4')
											? messages.issuesDeployedIssueTerminologyRefreshM4
											: messages.issuesDeployed,
										{
											issueCount: issueIds.length,
										},
									)}
								</Text>
							</Box>
						</>
					)}
				</Text>
			</Inline>
			<PopupOverflowSection>
				<PopupKeyline />
				<PopupOverflowButton
					component={Link}
					appearance="link"
					// @ts-expect-error - TS2322 - Problem with `Button` types, in practice this gets forwarded to the `Link`
					prefetch="hover"
					params={{
						projectKey: version.project.key,
					}}
					query={{ startDate: '-3m', endDate: 'now', fixVersions: version.name }}
					to={deploymentsRoute}
					onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'latestDeploymentInVersionDetailsTooltipLink');
					}}
				>
					{formatMessage(messages.viewAllDeployments)}
				</PopupOverflowButton>
			</PopupOverflowSection>
		</PopupContainer>
	);
};

const headerStatusIconAndLozengeStyles = xcss({
	flex: 1,
	justifyContent: 'end',
	alignItems: 'center',
});

const headerStatusIconStyles = xcss({
	marginRight: 'space.100',
	justifyContent: 'center',
	alignItems: 'center',
});

const environmentStyles = xcss({
	marginBottom: 'space.100',
});

const environmentDetailStyles = xcss({
	height: '16px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	marginBottom: 'space.100',
	marginTop: 'space.0',
});

const environmentDetailBetweenMarginStyles = xcss({
	marginLeft: 'space.050',
});
